import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

export default function PlatformMessages() {
  const [tenants, setTenants] = useState([]);
  const [messages, setMessages] = useState([]);
  const [form, setForm] = useState({
    tenant_id: "", category: "general_announcement", severity: "info",
    title: "", body: "", action_url: "",
  });

  const load = async () => {
    const [t, m] = await Promise.all([
      api.get("/platform/tenants", { params: { limit: 100 } }),
      api.get("/platform/messages", { params: { limit: 50 } }),
    ]);
    setTenants(t.data.items);
    setMessages(m.data.items);
  };

  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, tenant_id: form.tenant_id || null };
      await api.post("/platform/messages", body);
      toast.success("Message sent");
      setForm({ ...form, title: "", body: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="label-overline">Platform</div>
        <h1 className="text-3xl font-black tracking-tight mt-1">Tenant Messages</h1>
        <p className="text-slate-600 mt-1">Billing notices, app updates & warnings to truck stop owners.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={send} className="bg-white border border-slate-200 p-5 lg:col-span-1 space-y-4">
          <div className="label-overline">Compose</div>
          <div>
            <Label className="label-overline">Target tenant</Label>
            <select data-testid="msg-tenant"
                    className="w-full mt-1 h-10 border border-slate-300 rounded px-2 font-mono"
                    value={form.tenant_id}
                    onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}>
              <option value="">— Broadcast to all —</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.business_name} ({t.slug})</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-overline">Category</Label>
              <select data-testid="msg-category"
                      className="w-full mt-1 h-10 border border-slate-300 rounded px-2 font-mono"
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="general_announcement">general_announcement</option>
                <option value="app_update">app_update</option>
                <option value="billing_notice">billing_notice</option>
                <option value="grace_warning">grace_warning</option>
                <option value="suspension_warning">suspension_warning</option>
                <option value="plan_change">plan_change</option>
              </select>
            </div>
            <div>
              <Label className="label-overline">Severity</Label>
              <select data-testid="msg-severity"
                      className="w-full mt-1 h-10 border border-slate-300 rounded px-2 font-mono"
                      value={form.severity}
                      onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                <option value="info">info</option>
                <option value="warning">warning</option>
                <option value="critical">critical</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="label-overline">Title</Label>
            <Input data-testid="msg-title" className="rounded h-10 mt-1"
                   required value={form.title}
                   onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <Label className="label-overline">Body</Label>
            <Textarea data-testid="msg-body" className="rounded mt-1" rows={5}
                      required value={form.body}
                      onChange={(e) => setForm({ ...form, body: e.target.value })} />
          </div>
          <Button type="submit" data-testid="msg-submit"
                  className="w-full rounded bg-blue-700 hover:bg-blue-700">Send</Button>
        </form>

        <div className="lg:col-span-2 bg-white border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200 label-overline">Recent messages</div>
          <div className="divide-y divide-slate-100">
            {messages.length === 0 && (
              <div className="px-4 py-6 text-center text-slate-500">No messages sent yet</div>
            )}
            {messages.map((m) => (
              <div key={m.id} className="px-4 py-3">
                <div className="flex items-center gap-2 text-xs">
                  <span className="pill" style={{
                    borderColor: m.severity === "critical" ? "#DC2626"
                      : m.severity === "warning" ? "#EAB308" : "#2563EB",
                    color: m.severity === "critical" ? "#DC2626"
                      : m.severity === "warning" ? "#A16207" : "#2563EB",
                  }}>{m.severity}</span>
                  <span className="font-mono text-slate-500">{m.category}</span>
                  <span className="ml-auto font-mono text-slate-500">{fmtDate(m.created_at)}</span>
                </div>
                <div className="font-semibold mt-1">{m.title}</div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{m.body}</div>
                <div className="text-xs text-slate-500 mt-1 font-mono">
                  {m.tenant_id ? `→ tenant ${m.tenant_id.slice(0, 8)}…` : "→ broadcast to all"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
